import { useState, useEffect } from 'react';
import styles from './RandomnessServiceCard.module.css';

interface RandomnessBeacon {
  mode: 'native' | 'observed';
  height: number;
  output?: string;
  proof?: string;
  producer?: string;
  timestamp_ms?: number;
  verified?: boolean;
  feeReceiptsTotal?: number;
  eventCount?: number;
  eventDigest?: string;
  metaRoot?: string;
  source: string;
}

interface StateRootsPayload {
  global_height?: number;
  fee_receipts_total?: number;
  event_count?: number;
  event_digest?: string;
  meta_root?: string;
}

interface MetricsPayload {
  fees?: {
    total_receipts?: number;
  };
}

export function RandomnessServiceCard() {
  const [beacon, setBeacon] = useState<RandomnessBeacon | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLatestBeacon();
    // Refresh every 10 seconds
    const interval = setInterval(fetchLatestBeacon, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchLatestBeacon = async () => {
    const apiBase = import.meta.env.VITE_API_URL || 'https://api.rivellum.network';

    try {
      const response = await fetch(`${apiBase}/randomness/beacon/latest`);
      if (response.ok) {
        const data = await response.json();
        setBeacon({
          ...data,
          mode: 'native',
          source: '/randomness/beacon/latest',
        });
        setError(null);
        return;
      }

      // Fallback: derive a beacon-like card view from the same /v1 RPC routes explorer uses.
      const [stateRes, metricsRes] = await Promise.all([
        fetch(`${apiBase}/v1/state_roots`),
        fetch(`${apiBase}/v1/metrics`),
      ]);

      if (!stateRes.ok || !metricsRes.ok) {
        throw new Error(`Failed to fetch beacon (status ${response.status})`);
      }

      const state = (await stateRes.json()) as StateRootsPayload;
      const metrics = (await metricsRes.json()) as MetricsPayload;

      const fallbackHeight = Math.max(
        Number(state.global_height ?? 0),
        Number(state.fee_receipts_total ?? 0),
        Number(state.event_count ?? 0),
        Number(metrics.fees?.total_receipts ?? 0),
      );

      const output = state.event_digest || state.meta_root || 'unavailable';
      const proof = state.meta_root || state.event_digest || 'unavailable';

      setBeacon({
        mode: 'observed',
        height: Number.isFinite(fallbackHeight) ? fallbackHeight : 0,
        feeReceiptsTotal: Number(state.fee_receipts_total ?? 0),
        eventCount: Number(state.event_count ?? 0),
        eventDigest: state.event_digest || 'unavailable',
        metaRoot: state.meta_root || 'unavailable',
        output,
        proof,
        verified: false,
        source: '/v1/state_roots + /v1/metrics',
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>
            <span className={styles.icon}>🎲</span>
            VRF
          </h3>
          <p className={styles.subtitle}>Repo-backed VRF primitives and live chain observations</p>
        </div>
        <div className={styles.statusBadge} data-status="active">
          {beacon?.mode === 'native' ? 'Native Endpoint' : 'Observed RPC'}
        </div>
      </div>

      <div className={styles.content}>
        {loading && (
          <div className={styles.loading}>Loading VRF data...</div>
        )}

        {error && (
          <div className={styles.error}>
            ⚠️ {error}
          </div>
        )}

        {beacon && !loading && (
          <div className={styles.beaconData}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Latest Height:</span>
              <span className={styles.value}>{beacon.height}</span>
            </div>

            <div className={styles.infoRow}>
              <span className={styles.label}>Source:</span>
              <span className={styles.value}>{beacon.source}</span>
            </div>

            {beacon.mode === 'native' ? (
              <>
                <div className={styles.infoRow}>
                  <span className={styles.label}>VRF Output:</span>
                  <code className={styles.valueCode}>
                    {beacon.output?.substring(0, 16) || 'unavailable'}...
                  </code>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.label}>Verification:</span>
                  <span className={styles.value}>
                    {beacon.verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Fee Receipts:</span>
                  <span className={styles.value}>{beacon.feeReceiptsTotal ?? 0}</span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.label}>Event Count:</span>
                  <span className={styles.value}>{beacon.eventCount ?? 0}</span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.label}>Event Digest:</span>
                  <code className={styles.valueCode}>
                    {beacon.eventDigest?.substring(0, 16) || 'unavailable'}...
                  </code>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.label}>Meta Root:</span>
                  <code className={styles.valueCode}>
                    {beacon.metaRoot?.substring(0, 16) || 'unavailable'}...
                  </code>
                </div>
              </>
            )}
          </div>
        )}

        <div className={styles.info}>
          <p>
            Rivellum VRF is exposed to Mist contracts through `vrf_seed()`, `vrf_random_u64()`,
            and `vrf_random_range(min, max)`. The examples below are exact Mist code and were
            verified against the current compiler in this repo.
          </p>
        </div>

        <div className={styles.usage}>
          <h4 className={styles.usageTitle}>Exact Mist VRF Usage</h4>
          <p className={styles.contractDesc}>
            Minimal contract showing the real callable VRF surface in Mist.
          </p>
          <pre className={styles.codeBlock}>
      {`contract VrfProbe
    state owner: Address
    state last: Int

    function init()
        owner = caller
        last = 0

    function draw()
        require caller == owner

        let seed = vrf_seed()
        let number = vrf_random_u64()
        let digit = vrf_random_range(0, 9)

        last = digit
        emit Drawn(caller, number)`}
          </pre>
        </div>

        <div className={styles.usage}>
          <h4 className={styles.usageTitle}>Mist Lottery Example</h4>
          <p className={styles.contractDesc}>
            Real contract pattern from <code>mist/prefabs/daily_draw.mist</code> using new Mist bounded collections. The contract scans all tickets during finalize, computes the exact winner count itself, and immediately reserves each winner's payout balance by address. Nobody has to self-register as a winner, and slow claimers do not lose their reserved share.
          </p>
          <pre className={styles.codeBlock}>
      {`contract DailyDraw
    state owner: Address
    state asset_id: Address
    state ticket_price: Int
    state prize_pool: VaultRef

    state round_open: Bool
      state round_start_idx: Int
      state round_end_idx: Int
    state total_pool: Int
      state total_tickets: Int
    state winning_a: Int
    state winning_b: Int
    state winning_c: Int
    state winner_count: Int
    state per_winner_amount: Int
    state owner_fee: Int
    state reserved_winnings_total: Int

      state ticket_owner: List<Address, 1024>
      state ticket_a: List<Int, 1024>
      state ticket_b: List<Int, 1024>
      state ticket_c: List<Int, 1024>
      state reserved_payouts: Map<Address, Int, 1024>

      function init(payment_asset: Address, price: Int)
        require price > 0
        owner = caller
        asset_id = payment_asset
        ticket_price = price
        round_open = true
        round_start_idx = 0
        round_end_idx = 0
        total_pool = 0
        total_tickets = 0
        winner_count = 0
        per_winner_amount = 0
        owner_fee = 0
        reserved_winnings_total = 0
        winning_a = -1
        winning_b = -1
        winning_c = -1

      # Unlimited (bounded) ticket intake for the open round
    function buy_ticket(a: Int, b: Int, c: Int)
        require round_open == true
        require a >= 0 && a <= 9
        require b >= 0 && b <= 9
        require c >= 0 && c <= 9
        require list_len(ticket_owner) < 1024

        hold prize_pool = ticket_price asset asset_id from caller
        let _o = list_push(ticket_owner, caller)
        let _a = list_push(ticket_a, a)
        let _b = list_push(ticket_b, b)
        let _c = list_push(ticket_c, c)
        total_pool = total_pool + ticket_price
        total_tickets = total_tickets + 1
        emit TicketBought(caller, a, b, c, total_tickets)

    function buy_ticket_random()
        require round_open == true
        let rand_a = vrf_random_range(0, 9)
        let rand_b = vrf_random_range(0, 9)
        let rand_c = vrf_random_range(0, 9)
        buy_ticket(rand_a, rand_b, rand_c)

      # VRF draw + deterministic bounded scan over tickets
    function finalize_round()
        require caller == owner
        require round_open == true && total_pool > 0

        winning_a = vrf_random_range(0, 9)
        winning_b = vrf_random_range(0, 9)
        winning_c = vrf_random_range(0, 9)

        round_end_idx = list_len(ticket_owner)
        winner_count = 0
        for i in round_start_idx..round_end_idx
          let a = list_get(ticket_a, i)
          let b = list_get(ticket_b, i)
          let c = list_get(ticket_c, i)
          if a == winning_a && b == winning_b && c == winning_c
            winner_count = winner_count + 1

        require winner_count > 0

        let winner_pool = (total_pool * 80) / 100
        per_winner_amount = winner_pool / winner_count
        let reserved_winner_pool = per_winner_amount * winner_count
        owner_fee = total_pool - reserved_winner_pool

        for i in round_start_idx..round_end_idx
          let a = list_get(ticket_a, i)
          let b = list_get(ticket_b, i)
          let c = list_get(ticket_c, i)
          if a == winning_a && b == winning_b && c == winning_c
            let winner = list_get(ticket_owner, i)
            let reserved_for_winner = 0
            if map_contains(reserved_payouts, winner)
              reserved_for_winner = map_get(reserved_payouts, winner)
            let _reserved = map_put(reserved_payouts, winner, reserved_for_winner + per_winner_amount)
            reserved_winnings_total = reserved_winnings_total + per_winner_amount

        round_open = false
        emit RoundFinalized(caller, winning_a, winning_b, winning_c, winner_count, per_winner_amount)

      # Claim the caller's already-reserved balance
      function claim_prize()
        require map_contains(reserved_payouts, caller)
        let amount = map_get(reserved_payouts, caller)
        require amount > 0

        let _cleared = map_put(reserved_payouts, caller, 0)
        reserved_winnings_total = reserved_winnings_total - amount
        pay amount asset asset_id to caller from prize_pool

    function collect_owner_fee()
        require caller == owner && owner_fee > 0
        let fee = owner_fee
        owner_fee = 0
        pay fee asset asset_id to owner from prize_pool

      function start_next_round()
        require caller == owner
        require round_open == false

        round_open = true
        total_pool = 0
        winner_count = 0
        per_winner_amount = 0
        owner_fee = 0
        round_start_idx = round_end_idx
        winning_a = -1
        winning_b = -1
        winning_c = -1`}
          </pre>
        </div>

        <div className={styles.warning}>
          <strong>⚠️ Note:</strong> Rivellum code uses heights, receipts, and transaction-scoped
          VRF inputs. This UI does not assume a block explorer model because the repo code does not.
        </div>
      </div>
    </div>
  );
}
